import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

// ── Only countries with specific regulatory frameworks ────
// For all others → "Other" fallback uses NIST/OECD/ISO 42001
const SUPPORTED_COUNTRIES = [
  {
    group: 'Asia Pacific',
    countries: [
      { value: 'India',       label: 'India',        flag: '🇮🇳' },
      { value: 'Singapore',   label: 'Singapore',    flag: '🇸🇬' },
      { value: 'Australia',   label: 'Australia',    flag: '🇦🇺' },
      { value: 'New Zealand', label: 'New Zealand',  flag: '🇳🇿' },
      { value: 'Japan',       label: 'Japan',        flag: '🇯🇵' },
      { value: 'South Korea', label: 'South Korea',  flag: '🇰🇷' },
      { value: 'Hong Kong',   label: 'Hong Kong',    flag: '🇭🇰' },
    ]
  },
  {
    group: 'North America',
    countries: [
      { value: 'United States', label: 'United States', flag: '🇺🇸' },
      { value: 'Canada',        label: 'Canada',        flag: '🇨🇦' },
    ]
  },
  {
    group: 'Europe',
    countries: [
      { value: 'United Kingdom', label: 'United Kingdom', flag: '🇬🇧' },
      { value: 'Germany',        label: 'Germany',        flag: '🇩🇪' },
      { value: 'France',         label: 'France',         flag: '🇫🇷' },
      { value: 'Netherlands',    label: 'Netherlands',    flag: '🇳🇱' },
      { value: 'Ireland',        label: 'Ireland',        flag: '🇮🇪' },
      { value: 'Sweden',         label: 'Sweden',         flag: '🇸🇪' },
      { value: 'Switzerland',    label: 'Switzerland',    flag: '🇨🇭' },
    ]
  },
  {
    group: 'Middle East & Africa',
    countries: [
      { value: 'United Arab Emirates', label: 'United Arab Emirates', flag: '🇦🇪' },
      { value: 'Saudi Arabia',         label: 'Saudi Arabia',         flag: '🇸🇦' },
      { value: 'South Africa',         label: 'South Africa',         flag: '🇿🇦' },
    ]
  },
  {
    group: 'Other',
    countries: [
      { value: 'Other', label: 'Other — my country is not listed', flag: '🌍' },
    ]
  }
];

// ── State/Province — ONLY for countries where it matters ──
// Rule: include full state list ONLY when state-level law
// differs significantly (US, Canada, Australia, India)
// All other countries: free text city/region field
const STATE_REQUIRED_COUNTRIES = {
  'India': {
    label: 'State / Union Territory',
    options: [
      'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar',
      'Chhattisgarh','Goa','Gujarat','Haryana',
      'Himachal Pradesh','Jharkhand','Karnataka','Kerala',
      'Madhya Pradesh','Maharashtra','Manipur','Meghalaya',
      'Mizoram','Nagaland','Odisha','Punjab','Rajasthan',
      'Sikkim','Tamil Nadu','Telangana','Tripura',
      'Uttar Pradesh','Uttarakhand','West Bengal',
      'Delhi (NCT)','Chandigarh','Puducherry',
      'Jammu & Kashmir','Ladakh'
    ]
  },
  'United States': {
    label: 'State',
    note: 'Required — US state privacy laws vary significantly (CCPA, VCDPA, CPA etc.)',
    options: [
      'Alabama','Alaska','Arizona','Arkansas','California',
      'Colorado','Connecticut','Delaware','Florida','Georgia',
      'Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas',
      'Kentucky','Louisiana','Maine','Maryland','Massachusetts',
      'Michigan','Minnesota','Mississippi','Missouri','Montana',
      'Nebraska','Nevada','New Hampshire','New Jersey',
      'New Mexico','New York','North Carolina','North Dakota',
      'Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
      'South Carolina','South Dakota','Tennessee','Texas','Utah',
      'Vermont','Virginia','Washington','West Virginia',
      'Wisconsin','Wyoming','District of Columbia'
    ]
  },
  'Canada': {
    label: 'Province / Territory',
    note: 'Required — Quebec has distinct privacy law (Law 25)',
    options: [
      'Alberta','British Columbia','Manitoba',
      'New Brunswick','Newfoundland and Labrador',
      'Northwest Territories','Nova Scotia','Nunavut',
      'Ontario','Prince Edward Island','Quebec',
      'Saskatchewan','Yukon'
    ]
  },
  'Australia': {
    label: 'State / Territory',
    options: [
      'New South Wales','Victoria','Queensland',
      'Western Australia','South Australia','Tasmania',
      'Australian Capital Territory','Northern Territory'
    ]
  }
};

// ── Regulatory framework preview text ─────────────────────
const REGULATORY_PREVIEW = {
  'India':                 'DPDPA 2023 · IT Act 2000 · MeitY AI Advisory · CERT-In Guidelines',
  'United States':         'NIST AI RMF · FTC Guidelines · HIPAA/FERPA (sector) · State Privacy Laws',
  'United Kingdom':        'UK GDPR · Data Protection Act 2018 · ICO AI Guidance · FCA (FinTech)',
  'Canada':                'AIDA · PIPEDA · Directive on Automated Decision-Making · OSFI (FinTech)',
  'Australia':             'Privacy Act 1988 · APPs · APRA CPS 234 · ASD Essential Eight',
  'Singapore':             'PDPA · MAS TRM Guidelines · Model AI Governance Framework v2',
  'New Zealand':           'Privacy Act 2020 · Algorithm Charter · RBNZ AI Guidelines',
  'Japan':                 'APPI · METI AI Guidelines for Business · FSA AI Guidelines',
  'South Korea':           'PIPA · AI Industry Promotion Act 2024 · FSC AI Guidelines',
  'Hong Kong':             'PDPO · PCPD AI Guidelines 2024 · HKMA AI Guidelines',
  'Germany':               'EU AI Act (2024/1689) · GDPR · EU Data Act · DORA (FinTech)',
  'France':                'EU AI Act (2024/1689) · GDPR · CNIL AI Guidelines · DORA',
  'Netherlands':           'EU AI Act (2024/1689) · GDPR · DNB AI Framework · NIS2',
  'Ireland':               'EU AI Act (2024/1689) · GDPR · DPC Guidelines · DORA',
  'Sweden':                'EU AI Act (2024/1689) · GDPR · IMY Guidelines · NIS2',
  'Switzerland':           'Swiss revDSG · FINMA AI Circular · EU AI Act (alignment)',
  'United Arab Emirates':  'UAE PDPL · DIFC Data Protection Law · CBUAE AI Guidelines',
  'Saudi Arabia':          'PDPL · SDAIA AI Ethics · NCA Cybersecurity Framework',
  'South Africa':          'POPIA · FSCA AI Guidelines · SARB Prudential Standards',
  'Other':                 'NIST AI RMF · OECD AI Principles · ISO/IEC 42001 · G20 AI Principles'
};

// EU fallback applies to all EU countries not listed individually
const EU_COUNTRIES = [
  'Austria','Belgium','Bulgaria','Croatia','Cyprus',
  'Czech Republic','Denmark','Estonia','Finland','Greece',
  'Hungary','Italy','Latvia','Lithuania','Luxembourg',
  'Malta','Poland','Portugal','Romania','Slovakia',
  'Slovenia','Spain'
];

function getPreview(country) {
  if (REGULATORY_PREVIEW[country]) return REGULATORY_PREVIEW[country];
  if (EU_COUNTRIES.includes(country)) return REGULATORY_PREVIEW['Germany'];
  return REGULATORY_PREVIEW['Other'];
}

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // THE FIX: Parse URL parameters for direct email links!
  const queryParams = new URLSearchParams(location.search);
  const queryTier = queryParams.get('tier');

  // Dynamically capture the intent from the navigation state OR the URL parameter
  let formTitle = location.state?.fromButton || 'Create Your Account';
  if (queryTier === '0') formTitle = 'Start Free Trial';
  if (queryTier === '1') formTitle = 'Subscribe to Tier 1';
  if (queryTier === '2') formTitle = 'Request Tier 2 Sprint';

  // Capture the specific tier from either React state OR the URL parameter (defaults to 0)
  const initialTier = location.state?.targetTier !== undefined 
    ? location.state.targetTier 
    : (queryTier !== null ? parseInt(queryTier, 10) : 0);

  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    org_name: '',
    vertical: 'IT Services',
    size: '50-250',
    country: '',
    state: ''
  });

  const [isRegistered, setIsRegistered] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);


  // Use the captured initialTier here:
  const [selectedTier, setSelectedTier] = useState(initialTier);
  
  // NEW: State for showing password and reference for reCAPTCHA reset
  const [showPassword, setShowPassword] = useState(false);
  const recaptchaRef = useRef(null);

  const stateConfig = STATE_REQUIRED_COUNTRIES[form.country] || null;

  const handleCountryChange = (country) => {
    setForm({ ...form, country, state: '' });
  };

  const handleClose = () => {
    navigate(location.state?.from || '/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Require CAPTCHA Validation
    if (!captchaToken) {
      setError('Please verify that you are a human.');
      setLoading(false);
      return;
    }

    if (!form.country) {
      setError('Please select your country');
      setLoading(false);
      return;
    }

    if (stateConfig && !form.state) {
      setError(`Please select your ${stateConfig.label}`);
      setLoading(false);
      return;
    }

    // Password Validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
    if (!passwordRegex.test(form.password)) {
      setError('Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character.');
      
      // Force reCAPTCHA to reset on frontend validation failure!
      if (recaptchaRef.current) recaptchaRef.current.reset();
      setCaptchaToken(null);
      
      setLoading(false);
      return;
    }


    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await axios.post(`${baseURL}/api/auth/register`, { ...form, captchaToken, tier: selectedTier });
      setIsRegistered(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
      // Force reCAPTCHA to reset if the submission fails so the token doesn't go stale
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaToken(null);
    }
    setLoading(false);
  };

  const update = (field, value) =>
    setForm({ ...form, [field]: value });

  if (isRegistered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 relative">
        <button 
          onClick={handleClose}
          className="absolute top-8 right-8 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close verification page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="max-w-md w-full p-8 bg-white rounded-xl shadow-lg text-center">
          <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
            ✉️
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>
          <p className="text-gray-600 mb-6">
            We sent a verification link to <span className="font-semibold text-gray-800">{form.email}</span>. 
            Please click the link to activate your account.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Didn't receive it? Check your spam folder.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 to-slate-900 flex items-center justify-center p-4 relative">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 relative">
        
        {/* Industry Standard Close Button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors p-1"
          aria-label="Close form"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
            <span className="text-white font-bold text-xl">AI</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">
            {formTitle}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            AI Governance Platform for SMEs
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* TIER SELECTION TOGGLE */}
          <div className="flex gap-3 mb-6">
            <button 
              type="button" 
              onClick={() => setSelectedTier(0)} 
              className={`flex-1 py-2.5 px-2 text-xs font-bold rounded-lg transition-all duration-200 flex flex-col items-center justify-center gap-1 ${
                selectedTier === 0 
                  ? 'border-2 border-blue-600 bg-blue-50 text-blue-800 shadow-sm' 
                  : 'border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <span className="text-lg">⏱️</span>
              <span>Free Trial</span>
            </button>

            <button 
              type="button" 
              onClick={() => setSelectedTier(1)} 
              className={`flex-1 py-2.5 px-2 text-xs font-bold rounded-lg transition-all duration-200 flex flex-col items-center justify-center gap-1 ${
                selectedTier === 1 
                  ? 'border-2 border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm' 
                  : 'border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <span className="text-lg">🔓</span>
              <span>Tier 1</span>
            </button>

            <button 
              type="button" 
              onClick={() => setSelectedTier(2)} 
              className={`flex-1 py-2.5 px-2 text-xs font-bold rounded-lg transition-all duration-200 flex flex-col items-center justify-center gap-1 ${
                selectedTier === 2 
                  ? 'border-2 border-purple-500 bg-purple-50 text-purple-800 shadow-sm' 
                  : 'border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
               <span className="text-lg">🚀</span>
               <span>Tier 2</span>
            </button>
          </div>

          {/* Name + Org */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Full Name *
              </label>
              <input type="text" required
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your full name"
                value={form.full_name}
                onChange={e => update('full_name', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Organisation *
              </label>
              <input type="text" required
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Company name"
                value={form.org_name}
                onChange={e => update('org_name', e.target.value)} />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Email Address *
            </label>
            <input type="email" required
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="you@company.com"
              value={form.email}
              onChange={e => update('email', e.target.value)} />
          </div>

          {/* Password with Visibility Toggle */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Password *
            </label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} required
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 [&::-ms-reveal]:hidden [&::-webkit-reveal]:hidden"
                placeholder="Create a strong password"
                value={form.password}
                onChange={e => update('password', e.target.value)} />
              
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              Must be at least 8 characters and contain an uppercase letter, a lowercase letter, a number, and a special character.
            </p>
          </div>

          {/* Industry + Size */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Industry *
              </label>
              <select
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.vertical}
                onChange={e => update('vertical', e.target.value)}>
                <option value="IT Services">IT Services</option>
                <option value="FinTech">FinTech</option>
                <option value="Healthcare">Healthcare</option>
                <option value="EdTech">EdTech</option>
                <option value="SaaS">SaaS</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Company Size *
              </label>
              <select
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.size}
                onChange={e => update('size', e.target.value)}>
                <option value="10-50">10–50 employees</option>
                <option value="50-250">50–250 employees</option>
                <option value="250-500">250–500 employees</option>
                <option value="500-750">500–750 employees</option>
                <option value="750+">750+ employees</option>
              </select>
            </div>
          </div>

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Country *{' '}
              <span className="text-slate-400 font-normal text-xs">
                (sets your AI regulatory framework)
              </span>
            </label>
            <select
              required
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.country}
              onChange={e => handleCountryChange(e.target.value)}>
              <option value="">— Select Country —</option>
              {SUPPORTED_COUNTRIES.map(group => (
                <optgroup key={group.group} label={group.group}>
                  {group.countries.map(c => (
                    <option key={c.value} value={c.value}>
                      {c.flag} {c.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* State/Province */}
          {form.country && (
            <div>
              {stateConfig ? (
                <>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {stateConfig.label} *
                  </label>
                  {stateConfig.note && (
                    <p className="text-xs text-amber-600 mb-1">
                      ⚠ {stateConfig.note}
                    </p>
                  )}
                  <select
                    required
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.state}
                    onChange={e => update('state', e.target.value)}>
                    <option value="">— Select {stateConfig.label} —</option>
                    {stateConfig.options.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    City / Region{' '}
                    <span className="text-slate-400 font-normal text-xs">
                      (optional)
                    </span>
                  </label>
                  <input type="text"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Dubai, London, Tokyo"
                    value={form.state}
                    onChange={e => update('state', e.target.value)} />
                </>
              )}
            </div>
          )}

          {/* Regulatory Framework Preview */}
          {form.country && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <div className="text-xs font-semibold text-blue-700 mb-1">
                🌍 Regulatory Framework Applied —{' '}
                {form.country}
                {form.vertical !== 'IT Services' && (
                  <span className="text-blue-500 font-normal ml-1">
                    · {form.vertical}
                  </span>
                )}
              </div>
              <div className="text-xs text-blue-800 leading-relaxed">
                {getPreview(form.country)}
              </div>
              
              {form.country === 'Other' && (
                <div className="text-xs text-amber-700 mt-2 bg-amber-50 rounded px-2 py-1">
                  Your country will use international baseline standards.
                  Consult local legal counsel for jurisdiction-specific obligations.
                </div>
              )}
            </div>
          )}

          {/* Real-time Google reCAPTCHA v2 with ref integration */}
          <div className="mt-4 flex justify-center">
             <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY}
                onChange={(token) => setCaptchaToken(token)}
             />
          </div>

          <button type="submit" disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50 mt-4 shadow-sm">
            {loading 
              ? 'Processing...' 
              : selectedTier === 0
                ? 'Start 7-Day Free Trial' 
                : selectedTier === 1
                  ? 'Create Account & Request Tier 1'
                  : 'Create Account & Request Tier 2'}
          </button>
        </form>

        <p className="text-center text-sm text-slate-500 mt-6">
          Already have an account?{' '}
          <Link to="/login" state={{ from: location.state?.from }}
            className="text-blue-600 font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}